import {CheckoutOrder} from '@platform/checkout/domain/models/checkout-order.model';
import {
  PaymentDTO,
  PaymentGateway,
  PaymentMethod,
  PaymentProvider,
  PaymentProps,
  PaymentStatus,
  PaymentType,
  SupplementDTO
} from '../model/payment.model';

export class Payment {
  private readonly _amount: number;
  private readonly _order: CheckoutOrder;
  private readonly _method: PaymentMethod;
  private readonly _gateway: PaymentGateway;
  private readonly _provider: PaymentProvider;
  private readonly _paymentType: PaymentType;
  private readonly _walletId?: string;
  private readonly _supplement?: SupplementDTO;

  private _status: PaymentStatus;
  private _transactionId?: string;
  private _error?: string;
  private _sourceId?: string;

  constructor(dto: PaymentProps) {
    this._amount = dto.amount;
    this._order = dto.order;
    this._method = dto.method;
    this._gateway = dto.gateway;
    this._error = dto.error;
    this._sourceId = dto.sourceId;
    this._walletId = dto.walletId;
    this._supplement = dto.supplement;
    this._transactionId = dto.transactionId;
    this._status = dto.status ?? PaymentStatus.IDLE;
    this._paymentType = dto.paymentType ?? PaymentType.FULL;
    this._provider = dto.provider ?? this.resolveDefaultProvider(this._method);

    this.validate();
  }

  get order(): CheckoutOrder {
    return this._order;
  }

  get amount(): number {
    return this._amount;
  }

  get method(): PaymentMethod {
    return this._method;
  }

  get gateway(): PaymentGateway {
    return this._gateway;
  }

  get provider(): PaymentProvider {
    return this._provider;
  }

  get paymentType(): PaymentType {
    return this._paymentType;
  }

  get status(): PaymentStatus {
    return this._status;
  }

  get error(): string | undefined {
    return this._error;
  }

  get transactionId(): string | undefined {
    return this._transactionId;
  }

  get sourceId(): string | undefined {
    return this._sourceId;
  }

  get walletId(): string | undefined {
    return this._walletId;
  }

  get supplement(): SupplementDTO | undefined {
    return this._supplement;
  }

  markAsProcessing(): void {
    this._status = PaymentStatus.PROCESSING;
    this._error = undefined;
  }

  markAsCheckout(transactionId: string): void {
    this._status = PaymentStatus.CHECKOUT;
    this._transactionId = transactionId;
    this._error = undefined;
  }

  markAsSuccess(): void {
    this._status = PaymentStatus.SUCCESS;
    this._error = undefined;
  }

  markAsFailed(error: string): void {
    this._status = PaymentStatus.FAILED;
    this._error = error;
  }

  assignSourceId(sourceId: string): void {
    this._sourceId = sourceId;
  }

  private resolveDefaultProvider(method: PaymentMethod): PaymentProvider {
    return method === PaymentMethod.CARD ? PaymentProvider.ADYEN : PaymentProvider.DEFAULT;
  }

  private validate(): void {
    const isExempt = [PaymentMethod.COMPLEMENT, PaymentMethod.CONSUMPTION].includes(this._method) || !!this._supplement;

    if (!isExempt && this._amount <= 0) {
      throw new Error('[Payment Domain] Regular payments must have a positive amount');
    }

    if (this._method === PaymentMethod.CARD && this._provider !== PaymentProvider.ADYEN) {
      throw new Error('[Payment Domain] Card payments must use ADYEN provider');
    }
  }

  toDTO(): PaymentDTO {
    return {
      amount: this._amount,
      order: this._order,
      method: this._method,
      gateway: this._gateway,
      provider: this._provider,
      status: this._status,
      paymentType: this._paymentType,
      transactionId: this._transactionId,
      error: this._error,
      sourceId: this._sourceId,
      walletId: this._walletId,
      supplement: this._supplement,
    };
  }
}
